import React from 'react';
import { useState } from 'react';
import { MoreVertical, Star, Download, Share2, FileText, Image, Video, Music, File, Users } from 'lucide-react';
import { FileItem as FileItemType } from '../types';
import { formatFileSize, formatDate, isImageFile } from '../utils/fileUtils';
import { ContextMenu } from './ContextMenu';

interface FileItemProps {
  file: FileItemType;
  viewMode: 'grid' | 'list';
  isTrashView?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  onDelete: (id: string) => void;
  onRestore?: (id: string) => void;
  onPermanentDelete?: (id: string) => void;
  onToggleStar: (id: string) => void;
  onShare: (id: string) => void;
  onDuplicate: (id: string) => void;
  onMoveTo: (id: string) => void;
  onRename: (id: string) => void;
  onPreview: (file: FileItemType) => void;
  onDetails?: (file: FileItemType) => void;
  isGlobalDarkMode?: boolean;
  currentUserName?: string;
  currentUserAvatarUrl?: string | null;
  currentUserId?: string;
}

export const FileItem: React.FC<FileItemProps> = ({
  file,
  viewMode,
  isTrashView = false,
  selectionMode = false,
  isSelected,
  onSelect,
  onDelete,
  onRestore,
  onPermanentDelete,
  onToggleStar,
  onShare,
  onDuplicate,
  onMoveTo,
  onRename,
  onPreview,
  onDetails,
  isGlobalDarkMode = false,
  currentUserName,
  currentUserAvatarUrl,
  currentUserId
}) => {
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({ isOpen: false, position: { x: 0, y: 0 } });


  const handleKebabClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      position: { x: rect.right - 200, y: rect.bottom + 5 }
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect?.(file.id, !isSelected);
  };

  const handleContextMenuClose = () => {
    setContextMenu({ ...contextMenu, isOpen: false });
  };

  const handleFileClick = (e: React.MouseEvent) => {
    // Don't open preview if context menu is open or if clicking on interactive elements
    if (contextMenu.isOpen) {
      return;
    }

    // Check if the click target is an interactive element (checkbox, button, etc.)
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }

    if (selectionMode && onSelect) {
      onSelect(file.id, !isSelected);
      return;
    }

    onPreview(file);
  };

  const getFileIcon = (size = 20) => {
    if (!file.mimeType) return <File size={size} className="text-gray-500" />;

    if (file.mimeType.startsWith('image/')) return <Image size={size} className="text-green-500" />;
    if (file.mimeType.startsWith('video/')) return <Video size={size} className="text-red-500" />;
    if (file.mimeType.startsWith('audio/')) return <Music size={size} className="text-purple-500" />;
    if (file.mimeType.includes('pdf')) return <FileText size={size} className="text-red-600" />;
    if (file.mimeType.includes('document')) return <FileText size={size} className="text-blue-600" />;
    if (file.mimeType.includes('spreadsheet')) return <FileText size={size} className="text-green-600" />;

    return <File size={size} className="text-gray-500" />;
  };

  if (viewMode === 'list') {
    return (
      <div className={`grid grid-cols-12 gap-4 px-6 py-3 hover:bg-accent border-border border-b transition-colors duration-200 group ${isSelected ? 'bg-workspace-accent/10' : ''}`}
        onClick={handleFileClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="col-span-4 flex items-center gap-3 min-w-0">
          {onSelect && selectionMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                onSelect?.(file.id, !isSelected);
              }}
              className="w-4 h-4 text-workspace-accent border-border rounded focus:ring-workspace-accent"
            />
          )}

          {(isImageFile(file.mimeType) || file.mimeType?.includes('svg')) && file.url ? (
            file.mimeType?.includes('svg') ? (
              <object
                data={file.url}
                type="image/svg+xml"
                className="w-6 h-6 object-contain"
                aria-label={file.name}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  {getFileIcon()}
                </div>
              </object>
            ) : (
              <img
                src={file.url}
                alt={file.name}
                className="w-6 h-6 object-cover rounded"
              />
            )
          ) : (
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="text-gray-600">
                {getFileIcon()}
              </div>
            </div>
          )}

          <span className="text-sm text-foreground truncate">{file.name}</span>
          {file.shared && (
            <span title="Shared" className="flex-shrink-0">
              <Users size={12} className="text-workspace-accent" />
            </span>
          )}
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
            {(file.ownerAvatarUrl || (file.ownerId === currentUserId && currentUserAvatarUrl)) ? (
              <img
                src={file.ownerAvatarUrl || currentUserAvatarUrl || ''}
                alt="Owner"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white text-xs font-medium">
                {(file.ownerName || currentUserName || 'User')
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </span>
            )}
          </div>
          <span className="text-sm text-muted-foreground">{file.ownerName || currentUserName || 'User'}</span>
        </div>

        <div className="col-span-3 text-sm text-muted-foreground">
          {formatDate(file.modifiedAt)} {file.modifiedBy || file.ownerName || currentUserName || 'User'}
        </div>

        <div className="col-span-2 text-sm text-muted-foreground">
          {file.size ? formatFileSize(file.size) : '—'}
        </div>

        <div className="col-span-1 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar(file.id);
            }}
            className={`p-1.5 rounded hover:bg-accent transition-colors duration-200 ${file.starred ? 'text-yellow-500' : 'text-muted-foreground'}`}
          >
            <Star size={14} fill={file.starred ? 'currentColor' : 'none'} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('Download', file.id);
            }}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors duration-200"
          >
            <Download size={14} />
          </button>

          <button
            onClick={handleKebabClick}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors duration-200"
          >
            <MoreVertical size={14} />
          </button>
        </div>

        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          isFolder={false}
          isTrashView={isTrashView}
          isStarred={file.starred}
        isShared={file.shared}
          onShare={() => onShare(file.id)}
          onDownload={() => console.log('Download', file.id)}
          onRename={() => onRename(file.id)}
          onToggleStar={() => onToggleStar(file.id)}
          onDuplicate={() => onDuplicate(file.id)}
          onMoveTo={() => onMoveTo(file.id)}
          onDelete={() => onDelete(file.id)}
          onRestore={() => onRestore?.(file.id)}
          onPermanentDelete={() => onPermanentDelete?.(file.id)}
          onPreview={() => onPreview(file)}
          onDetails={() => onDetails?.(file)}
          onClose={handleContextMenuClose}
        />
      </div>
    );
  }

  return (
    <div className="relative group">
      <div className={`bg-card rounded-lg border hover:border-border/80 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden ${isSelected ? 'border-workspace-accent ring-1 ring-workspace-accent/30' : 'border-border'}`}
        onClick={handleFileClick}
      >
        {onSelect && selectionMode && (
          <div className="absolute top-2 left-2 z-20">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-workspace-accent border-border rounded focus:ring-workspace-accent"
            />
          </div>
        )}

        {/* Top right buttons - Share, Download and Kebab menu */}
        <div className="absolute top-2 right-2 z-20 hidden group-hover:flex items-center gap-1 transition-all duration-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare(file.id);
            }}
            className="p-1.5 bg-card/90 hover:bg-card text-foreground rounded transition-colors duration-200 shadow-sm border border-border/50"
          >
            <Share2 size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('Download', file.id);
            }}
            className="p-1.5 bg-card/90 hover:bg-card text-foreground rounded transition-colors duration-200 shadow-sm border border-border/50"
          >
            <Download size={14} />
          </button>
          <button
            onClick={handleKebabClick}
            className="p-1.5 bg-card/90 hover:bg-card text-foreground rounded transition-colors duration-200 shadow-sm border border-border/50"
          >
            <MoreVertical size={14} />
          </button>
        </div>

        {/* Thumbnail with overlay */}
        <div className="relative aspect-square overflow-hidden rounded-t-lg">
          {(isImageFile(file.mimeType) || file.mimeType?.includes('svg')) && file.url ? (
            file.mimeType?.includes('svg') ? (
              <object
                data={file.url}
                type="image/svg+xml"
                className="w-full h-full object-contain"
                aria-label={file.name}
              >
                <div className="w-full h-full flex items-center justify-center bg-muted rounded">
                  <svg className="w-12 h-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </object>
            ) : (
              <img
                src={file.url}
                alt={file.name}
                className="w-full h-full object-cover"
              />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              {getFileIcon(48)}
            </div>
          )}

          {/* Dark overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none"></div>

          {/* Shared badge */}
          {file.shared && (
            <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-workspace-accent/90 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow">
              <Users size={9} />
              <span>Shared</span>
            </div>
          )}

          {/* File name and metadata overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pt-3 pb-3 text-white">
            <div className="transition-transform duration-200 ease-in-out group-hover:-translate-y-2">
              <h3 className="font-medium text-sm truncate drop-shadow-md">{file.name}</h3>
              <div className="hidden group-hover:flex items-center gap-2 text-xs text-white/90 transition-all duration-200 drop-shadow-md mt-1.5">
                <span>{formatDate(file.modifiedAt)}</span>
                {file.size && (
                  <>
                    <span>•</span>
                    <span>{formatFileSize(file.size)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContextMenu
        isOpen={contextMenu.isOpen}
        onClose={handleContextMenuClose}
        position={contextMenu.position}
        isFolder={false}
        isTrashView={isTrashView}
        isStarred={file.starred}
        isShared={file.shared}
        onShare={() => onShare(file.id)}
        onDownload={() => console.log('Download', file.id)}
        onRename={() => onRename(file.id)}
        onToggleStar={() => onToggleStar(file.id)}
        onDuplicate={() => onDuplicate(file.id)}
        onMoveTo={() => onMoveTo(file.id)}
        onDelete={() => onDelete(file.id)}
        onRestore={() => onRestore?.(file.id)}
        onPermanentDelete={() => onPermanentDelete?.(file.id)}
        onPreview={() => onPreview(file)}
        onDetails={() => onDetails?.(file)}
        isGlobalDarkMode={isGlobalDarkMode}
      />
    </div>
  );
};